import { useState, useEffect } from 'react';
import { AlertTriangle, ArrowDownCircle, ArrowUpCircle, Package } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import api, { formatDate } from '../../lib/api';
import { toast } from 'sonner';

const AdminStock = () => {
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [lowStockRes, movementsRes] = await Promise.all([
          api.get('/admin/stock/low'),
          api.get('/admin/stock/movements'),
        ]);
        setLowStockProducts(lowStockRes.data);
        setMovements(movementsRes.data);
      } catch (error) {
        toast.error('Erro ao carregar dados de estoque');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <AdminLayout title="Controle de Estoque">
      <Tabs defaultValue="alerts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="alerts" data-testid="stock-alerts-tab">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Alertas ({lowStockProducts.length})
          </TabsTrigger>
          <TabsTrigger value="movements" data-testid="stock-movements-tab">
            <ArrowDownCircle className="w-4 h-4 mr-2" />
            Movimentações
          </TabsTrigger>
        </TabsList>

        {/* Low Stock Alerts */}
        <TabsContent value="alerts">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-warning" />
                Produtos com Estoque Baixo
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground text-center py-8">Carregando...</p>
              ) : lowStockProducts.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-success/10 rounded-full flex items-center justify-center">
                    <Package className="w-8 h-8 text-success" />
                  </div>
                  <p className="text-muted-foreground">
                    Todos os produtos estão com estoque adequado! 🎉
                  </p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="low-stock-list">
                  {lowStockProducts.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-4 bg-warning/5 border border-warning/20 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                          {product.images?.[0] ? (
                            <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Mínimo: {product.min_stock} unidades
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-destructive">{product.stock}</p>
                        <p className="text-xs text-muted-foreground">em estoque</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stock Movements */}
        <TabsContent value="movements">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading">Histórico de Movimentações</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground text-center py-8">Carregando...</p>
              ) : movements.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Nenhuma movimentação registrada
                </p>
              ) : (
                <div className="space-y-3" data-testid="movements-list">
                  {movements.map((movement) => (
                    <div
                      key={movement.id}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                    >
                      <div className={`p-2 rounded-full ${
                        movement.type === 'in' ? 'bg-success/10' : 'bg-destructive/10'
                      }`}>
                        {movement.type === 'in' ? (
                          <ArrowDownCircle className="w-5 h-5 text-success" />
                        ) : (
                          <ArrowUpCircle className="w-5 h-5 text-destructive" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{movement.product_name}</p>
                        <p className="text-sm text-muted-foreground">{movement.reason}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          movement.type === 'in' ? 'text-success' : 'text-destructive'
                        }`}>
                          {movement.type === 'in' ? '+' : '-'}{movement.quantity}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(movement.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminStock;
